<template>
  <div>
    <div class="account-statement-view">
      <div
        v-if="!autoPrint"
        class="actions mb-3 d-flex justify-content-end"
      >
        <b-button
          size="lg"
          variant="success"
          class="print-btn"
          :disabled="isGenerating"
          @click="generatePdf"
        >
          <feather-icon
            v-if="!isGenerating"
            icon="PrinterIcon"
            size="18"
            class="mr-50"
          />
          <b-spinner
            v-if="isGenerating"
            small
            class="mr-50"
          />
          <span v-if="isGenerating">جاري الطباعة...</span>
          <span v-else>طباعة</span>
        </b-button>
      </div>
      <div
        v-if="autoPrint && isGenerating"
        class="actions mb-3 d-flex justify-content-center"
      >
        <div class="text-center">
          <b-spinner />
          <p class="mt-2">
            جاري إعداد الطباعة...
          </p>
        </div>
      </div>

      <div
        id="printableContent"
        ref="printContent"
        class="statement-content"
        dir="rtl"
      >
        <div class="header">
          <div class="brand">
            <div class="brand-title">
              {{ getStatementTitle() }}
            </div>
            <div class="brand-sub">
              {{ getStatementSubtitle() }}
            </div>
            <div class="brand-info">
              {{ doctorName }}
            </div>
          </div>
          <div class="date-info">
            <div>تاريخ الطباعة: {{ printedAt }}</div>
            <div v-if="fromDate || toDate">
              الفترة:
              <span v-if="fromDate">من {{ fromDate }}</span>
              <span v-if="toDate">إلى {{ toDate }}</span>
            </div>
          </div>
        </div>

        <div class="meta-grid">
          <div class="meta-item">
            <span class="label">عدد الفواتير:</span>
            <span class="value">{{ data.length }}</span>
          </div>
          <div class="meta-item">
            <span class="label">المجموع الكلي:</span>
            <span class="value">{{ formatAmount(totalAmount) }}</span>
          </div>
        </div>

        <table class="statement-table">
          <thead>
            <tr>
              <th>التاريخ</th>
              <th>رقم الفاتورة</th>
              <th>اسم المريض</th>
              <th>الخدمة / العملية</th>
              <th>المبلغ</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, index) in data"
              :key="index"
            >
              <td>{{ getDate(item) }}</td>
              <td>{{ item.invoice_no || "-" }}</td>
              <td>{{ getPatientName(item) }}</td>
              <td>{{ getServiceName(item) }}</td>
              <td class="amount-cell">
                {{ formatAmount(getTotalAmount(item)) }}
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td
                :colspan="4"
                class="text-right font-weight-bold"
              >
                المجموع الكلي
              </td>
              <td class="font-weight-bold amount-cell">
                {{ formatAmount(totalAmount) }}
              </td>
            </tr>
          </tfoot>
        </table>

        <div class="footer-note">
          <div class="signature">
            <div class="sig-line" />
            <div class="sig-label">
              {{ $t("common.signature") }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BButton,
  BSpinner,
} from 'bootstrap-vue'

export default {
  name: 'DoctorDebtStatement',
  components: {
    BButton,
    BSpinner,
  },
  props: {
    data: {
      type: Array,
      required: true,
      default: () => [],
    },
    doctorName: {
      type: String,
      default: '',
    },
    statusType: {
      type: String,
      default: 'pending',
      validator: value => ['pending', 'paid'].includes(value),
    },
    fromDate: {
      type: String,
      default: '',
    },
    toDate: {
      type: String,
      default: '',
    },
    autoPrint: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isGenerating: false,
      printedAt: new Date().toLocaleString('ar-IQ'),
    }
  },
  computed: {
    totalAmount() {
      return this.data.reduce(
        (sum, item) => sum + parseFloat(this.getTotalAmount(item) || 0),
        0,
      )
    },
  },
  watch: {
    autoPrint(newVal) {
      if (newVal && this.data && this.data.length > 0) {
        this.$nextTick(() => {
          setTimeout(() => {
            this.generatePdf()
          }, 500)
        })
      }
    },
  },
  methods: {
    getStatementTitle() {
      // If auto-print (after status change), use "فاتورة دفع"
      if (this.autoPrint) {
        return 'فاتورة دفع'
      }
      // Otherwise, use original titles
      if (this.statusType === 'paid') {
        return 'كشف   الفواتير المدفوعة'
      }
      return 'كشف   الفواتير المعلقة'
    },
    getStatementSubtitle() {
      // If auto-print (after status change), use "فاتورة دفع"
      if (this.autoPrint) {
        return 'فاتورة دفع'
      }
      // Otherwise, use original subtitles
      if (this.statusType === 'paid') {
        return 'كشف الفواتير المدفوع'
      }
      return 'كشف الفواتير المعلق'
    },
    formatAmount(amount) {
      if (amount == null || amount === '') return '-'
      try {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'IQD',
          maximumFractionDigits: 0,
        }).format(Number(amount))
      } catch (e) {
        return amount
      }
    },
    getPatientName(item) {
      return (
        (item.patient_card
          && item.patient_card.patient
          && item.patient_card.patient.name)
        || '-'
      )
    },
    getServiceName(item) {
      // Try multiple sources for service/surgery name
      if (item.service && item.service.title) {
        return item.service.title
      }
      if (item.surgery && item.surgery.title) {
        return item.surgery.title
      }
      if (item.invoice_items && item.invoice_items.length > 0) {
        return item.invoice_items[0].name || '-'
      }
      return this.formatInvoiceType(item.type) || '-'
    },
    formatInvoiceType(type) {
      const typeMap = {
        invoice_out_clinic: 'عيادة خارجية',
        invoice_nicu: 'حضانة',
        invoice_insurance_surgery: 'عملية جراحية',
        invoice_emergency: 'طوارئ',
        invoice_patient_card_test: 'فحص',
        refund: 'استرداد',
      }
      return typeMap[type] || type
    },
    getTotalAmount(item) {
      return parseFloat(item.amount || 0)
    },
    getDate(item) {
      if (item.date) {
        const date = new Date(item.date)
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
      }
      if (item.created_at) {
        const date = new Date(item.created_at)
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
      }
      return '-'
    },
    generatePdf() {
      this.isGenerating = true
      const content = this.$refs.printContent
      if (!content) {
        this.isGenerating = false
        return
      }

      const printWindow = window.open('', '_blank')
      if (!printWindow) {
        // eslint-disable-next-line no-alert
        window.alert('يرجى السماح بالنوافذ المنبثقة لهذا الموقع')
        this.isGenerating = false
        return
      }

      const htmlContent = `
<!DOCTYPE html>
<html lang="ar" dir="rtl">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${this.getStatementTitle()}</title>
  <meta name="description" content="كشف حساب الطبيب">
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
      padding: 12px;
      color: #111827;
      line-height: 1.4;
      font-size: 11px;
      direction: rtl;
    }
    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 10px;
      border-bottom: 1px solid #e5e7eb;
      padding-bottom: 8px;
    }
    .brand-title {
      font-size: 16px;
      font-weight: 900;
      color: #111827;
      margin-bottom: 4px;
      text-transform: uppercase;
      letter-spacing: 1px;
    }
    .brand-sub {
      font-size: 13px;
      font-weight: 700;
      color: #1f2937;
    }
    .brand-info {
      font-size: 20px;
      font-weight: 700;
      color: #111827;
      margin-top: 8px;
    }
    .meta-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 8px;
      margin-bottom: 10px;
      padding: 8px;
      background: #f9fafb;
      border-radius: 8px;
    }
    .meta-item {
      display: flex;
      flex-direction: column;
    }
    .meta-item .label {
      font-size: 12px;
      color: #6b7280;
      font-weight: 600;
      text-transform: uppercase;
    }
    .meta-item .value {
      font-size: 13px;
      color: #111827;
      font-weight: 700;
      margin-top: 4px;
    }
    .statement-table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 16px;
    }
    .statement-table th,
    .statement-table td {
      padding: 6px 8px;
      text-align: right;
      border: 1px solid #e5e7eb;
    }
    .statement-table th {
      background: #f3f4f6;
      font-weight: 700;
      color: #111827;
      font-size: 11px;
    }
    .statement-table tfoot td {
      background: #f9fafb;
      font-weight: 700;
    }
    .amount-cell {
      text-align: left;
      direction: ltr;
    }
    .footer-note {
      margin-top: 20px;
      display: flex;
      justify-content: flex-end;
    }
    .signature {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
    .sig-line {
      width: 140px;
      height: 1px;
      background: #9ca3af;
      margin-bottom: 4px;
    }
    .sig-label {
      font-size: 12px;
      color: #4b5563;
      font-weight: 600;
    }
    @media print {
      body {
        padding: 10mm;
      }
      .statement-table {
        page-break-inside: auto;
      }
      .statement-table tr {
        page-break-inside: avoid;
      }
      .statement-table thead {
        display: table-header-group;
      }
    }
  </style>
</head>
<body>
  ${content.innerHTML}
  <script>
    window.onload = function() {
      window.print();
    };
  <\/script>
</body>
</html>
      `

      printWindow.document.write(htmlContent)
      printWindow.document.close()
      this.$emit('print-started')

      // Close modal after print dialog opens (if auto print)
      if (this.autoPrint) {
        setTimeout(() => {
          this.$emit('print-completed')
        }, 2000)
      }

      setTimeout(() => {
        this.isGenerating = false
      }, 1000)
    },
  },
}
</script>

<style scoped>
.account-statement-view {
  background: #fff;
  padding: 12px;
  color: #111827;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
  line-height: 1.4;
  font-size: 12px;
  direction: rtl;
}

.actions {
  position: sticky;
  top: 0;
  z-index: 100;
  background: #fff;
  padding: 15px 0;
  margin: -20px -20px 20px -20px;
  padding-left: 20px;
  padding-right: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-bottom: 2px solid #10b981;
}

.print-btn {
  font-weight: 700;
  font-size: 16px;
  padding: 12px 24px;
  box-shadow: 0 4px 6px rgba(16, 185, 129, 0.3);
  transition: all 0.3s ease;
}

.print-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(16, 185, 129, 0.4);
}

.print-btn:active:not(:disabled) {
  transform: translateY(0);
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 8px;
}

.brand-title {
  font-size: 18px;
  font-weight: 900;
  color: #111827;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 1px;
}
.brand-sub {
  font-size: 14px;
  font-weight: 800;
  color: #1f2937;
}

.brand-info {
  font-size: 28px;
  font-weight: 700;
  color: #111827;
  margin-top: 8px;
}

.meta-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 8px;
  margin-bottom: 12px;
  padding: 8px;
  background: #f9fafb;
  border-radius: 8px;
}

.meta-item {
  display: flex;
  flex-direction: column;
}

.meta-item .label {
  font-size: 12px;
  color: #6b7280;
  font-weight: 600;
  text-transform: uppercase;
}

.meta-item .value {
  font-size: 14px;
  color: #111827;
  font-weight: 700;
  margin-top: 4px;
}

.statement-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 16px;
}

.statement-table th,
.statement-table td {
  padding: 6px 8px;
  text-align: right;
  border: 1px solid #e5e7eb;
}

.statement-table th {
  background: #f3f4f6;
  font-weight: 700;
  color: #111827;
}

.statement-table tfoot td {
  background: #f9fafb;
  font-weight: 700;
}

.amount-cell {
  text-align: left;
  direction: ltr;
}

.footer-note {
  margin-top: 40px;
  display: flex;
  justify-content: flex-end;
}

.signature {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.sig-line {
  width: 200px;
  height: 1px;
  background: #9ca3af;
  margin-bottom: 4px;
}

.sig-label {
  font-size: 12px;
  color: #4b5563;
  font-weight: 600;
}
</style>
